import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckLoading, setAdminCheckLoading] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('study-app-user');
    const storedToken = localStorage.getItem('study-app-token');
    
    if (storedUser && storedToken) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      // Use is_admin from stored user data
      setIsAdmin(userData.is_admin === true || userData.role === 'admin');
    }
    setLoading(false);
  }, []);

  // Listen for session expiration events dispatched by api layer
  useEffect(() => {
    const handler = (e) => {
      // Clear auth state and redirect to login page
      logout();
      try {
        // Use location replace to avoid back navigation into protected pages
        window.location.replace('/login');
      } catch (err) {
        // fallback: set href
        window.location.href = '/login';
      }
    };

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('sessionExpired', handler);
    }

    return () => {
      if (typeof window !== 'undefined' && window.removeEventListener) {
        window.removeEventListener('sessionExpired', handler);
      }
    };
  }, []);

  const checkAdminStatus = useCallback(async () => {
    setAdminCheckLoading(true);
    try {
      // Get current user info which now includes role and is_admin fields
      const currentUser = await apiService.getCurrentUser();
      
      if (!currentUser || !currentUser.id) {
        setIsAdmin(false);
        return false;
      }
      
      // Check admin status from the response
      const adminStatus = currentUser.is_admin === true || currentUser.role === 'admin';
      setIsAdmin(adminStatus);
      
      // Update user data with full info
      const updatedUser = {
        ...user,
        id: currentUser.id,
        email: currentUser.email,
        username: currentUser.username,
        role: currentUser.role,
        is_admin: adminStatus,
        is_active: currentUser.is_active
      };
      setUser(updatedUser);
      localStorage.setItem('study-app-user', JSON.stringify(updatedUser));
      
      return adminStatus;
    } catch (error) {
      // Silently handle 403 Forbidden errors (expected for non-admin users)
      if (error.message && error.message.includes('403')) {
        setIsAdmin(false);
        return false;
      }
      
      // Log other errors (network issues, 500 errors, etc.)
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      return false;
    } finally {
      setAdminCheckLoading(false);
    }
  }, [user]);

  const register = async (email, username, password) => {
    try {
      const response = await apiService.register(email, username, password);
      
      // Registration successful - backend returns user object but NO token
      // User needs to login separately, so we don't set user/isAdmin state here
      
      return response;
    } catch (error) {
      throw new Error(error.message || 'Registration failed');
    }
  };

  const login = async (identifier, password) => {
    try {
      const response = await apiService.login(identifier, password);
      
      // Backend now returns user object with role and is_admin fields
      const userData = {
        id: response.user.id,
        email: response.user.email,
        username: response.user.username,
        role: response.user.role,
        is_admin: response.user.is_admin === true || response.user.role === 'admin',
        is_active: response.user.is_active,
        loginTime: new Date().toISOString()
      };
      
      setUser(userData);
      setIsAdmin(userData.is_admin);
      localStorage.setItem('study-app-user', JSON.stringify(userData));
      localStorage.setItem('study-app-token', response.access_token);
      
      return userData;
    } catch (error) {
      throw new Error(error.message || 'Login failed');
    }
  };

  const logout = () => {
    setUser(null);
    setIsAdmin(false);
    localStorage.removeItem('study-app-user');
    localStorage.removeItem('study-app-token');
  };

  const value = {
    user,
    register,
    login,
    logout,
    loading,
    isAdmin,
    adminCheckLoading,
    checkAdminStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}